import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import DraggableModal from "../DraggableModal";
import EmailDialog from "../EmailDialog";
import { htmlToPdfBlob, safeFilename } from "../../pdfRenderer";
import { escHtml, reportStyles, policyHeader, policyFooter, openPreviewWindow, downloadBlob } from "./auditCommon";
import { loadFromSupabase } from "../../supabaseDB";
import { appendDisclosureByAccount } from "../emailHelpers";

const T = {
  en: {
    title: "General Information & Flow Diagram",
    close: "Close", preview: "Preview", pdf: "Download PDF", email: "Email", busy: "Working…", sending: "Sending…",
    secGeneral: "General Information (NOP 205.201 & 205.401)",
    legalName: "Legal name", address: "Address", certNo: "Organic certificate #",
    nopId: "NOP Operation ID", certifier: "Certifier", scope: "Scope of operation", opType: "Operation type",
    opTypeVal: "Handling operation (handler/trader) — no physical contact with product",
    scopeVal: "Import and distribution of organic berries — blackberry, blueberry, raspberry",
    secFlow: "Operation Flow Diagram",
    loading: "Loading company data…",
    emailSubject: "General Information & Flow Diagram",
    emailBody: "Attached is the General Information & Flow Diagram for audit ",
  },
  es: {
    title: "Información General y Diagrama de Flujo",
    close: "Cerrar", preview: "Vista previa", pdf: "Descargar PDF", email: "Correo", busy: "Trabajando…", sending: "Enviando…",
    secGeneral: "Información General (NOP 205.201 & 205.401)",
    legalName: "Razón social", address: "Dirección", certNo: "Certificado orgánico #",
    nopId: "NOP Operation ID", certifier: "Certificadora", scope: "Alcance de la operación", opType: "Tipo de operación",
    opTypeVal: "Operación de manejo (handler/trader) — sin contacto físico con el producto",
    scopeVal: "Importación y distribución de berries orgánicos — mora, arándano, frambuesa",
    secFlow: "Diagrama de Flujo de la Operación",
    loading: "Cargando datos de la empresa…",
    emailSubject: "Información General y Diagrama de Flujo",
    emailBody: "Adjunto la Información General y Diagrama de Flujo para la auditoría ",
  },
};

function box(x,y,w,h,fill,stroke,title,sub) {
  var t = '<g><rect x="'+x+'" y="'+y+'" width="'+w+'" height="'+h+'" rx="8" fill="'+fill+'" stroke="'+stroke+'" stroke-width="1"/>';
  t += '<text x="'+(x+w/2)+'" y="'+(y+20)+'" text-anchor="middle" font-size="12" font-weight="700" fill="'+stroke+'">'+escHtml(title)+'</text>';
  var words = String(sub||"").split(" "); var line=""; var lines=[]; var max=34;
  for (var i=0;i<words.length;i++){ if((line+" "+words[i]).trim().length>max){lines.push(line.trim());line=words[i];}else{line=(line+" "+words[i]).trim();} }
  if(line) lines.push(line.trim());
  for (var j=0;j<lines.length && j<3;j++){ t += '<text x="'+(x+w/2)+'" y="'+(y+38+j*13)+'" text-anchor="middle" font-size="10" fill="#444">'+escHtml(lines[j])+'</text>'; }
  return t + '</g>';
}
function arrowH(x1,y,x2){ return '<line x1="'+x1+'" y1="'+y+'" x2="'+x2+'" y2="'+y+'" stroke="#555" stroke-width="1.5" marker-end="url(#ar)"/>'; }
function arrowV(x,y1,y2){ return '<line x1="'+x+'" y1="'+y1+'" x2="'+x+'" y2="'+y2+'" stroke="#555" stroke-width="1.5" marker-end="url(#ar)"/>'; }

function flowDiagramSvg(lang) {
  var ES = lang === "es";
  var L = ES ? {
    s1:["1. Productor certificado","Fruta orgánica empacada en campo"],
    s2:["2. Shipper recibe","Verifica empaque, etiquetado, trazabilidad; arma pallets"],
    s3:["3. Shipper embarca","Envía Factura, Proforma, Cert. Exportación, BOL a Agente + Importador"],
    s4:["4. Importador recibe docs","Asigna Lote# ligado a Factura, Proforma, Entry#, Cert.; notifica a Bodega"],
    s5:["5. Cruce de frontera","Transporte refrigerado, despacho aduanal"],
    s6:["6. Bodega recibe","Verifica cantidades vs docs, pallets vs BOL de origen, ingresa bajo Lote#"],
    s7:["7. Importador recibe PO","Genera PU# (Pick Up), envía a Cliente y Bodega"],
    s8:["8. Cliente recoge","Bodega verifica PU# del chofer vs registros; coincide carga, no coincide contacta Cliente"],
    s9:["9. Emite BOL de salida","Bodega entrega BOL al chofer, envía copia al Importador"],
    s10:["10. Importador finaliza","Envía BOL por correo al Cliente, envía Factura, espera pago"],
    s11:["11. Cliente recibe y paga","Orden completa; integridad orgánica mantenida"],
  } : {
    s1:["1. Certified Grower","Field-packed organic fruit"],
    s2:["2. Shipper receives","Verifies packing, labeling, traceability; builds pallets"],
    s3:["3. Shipper ships","Sends Invoice, Proforma, Export Cert, BOL to Broker + Importer"],
    s4:["4. Importer receives docs","Assigns Lot# linked to Invoice, Proforma, Entry#, Export Cert; notifies Warehouse"],
    s5:["5. Border crossing","Refrigerated transport, customs clearance"],
    s6:["6. Warehouse receives","Checks qty vs docs, verifies pallets vs origin BOL, enters under Lot#"],
    s7:["7. Importer gets PO","Generates PU# (Pick Up), sends to Client and Warehouse"],
    s8:["8. Client picks up","Warehouse verifies driver PU# vs records; match loads, no match contacts Client"],
    s9:["9. Outbound BOL issued","Warehouse hands BOL to driver, sends copy to Importer"],
    s10:["10. Importer finalizes","Emails BOL to Client, sends Invoice, awaits payment"],
    s11:["11. Client receives & pays","Order complete; organic integrity maintained"],
  };
  return ''
    + '<svg width="100%" viewBox="0 0 980 470" xmlns="http://www.w3.org/2000/svg" style="font-family:Arial,Helvetica,sans-serif">'
    + '<defs><marker id="ar" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse"><path d="M2 1L8 5L2 9" fill="none" stroke="#555" stroke-width="1.5"/></marker></defs>'
    + box(40,30,210,70,"#E1F5EE","#0F6E56",L.s1[0],L.s1[1])
    + box(290,30,210,70,"#E1F5EE","#0F6E56",L.s2[0],L.s2[1])
    + box(540,30,210,70,"#E1F5EE","#0F6E56",L.s3[0],L.s3[1])
    + arrowH(250,65,288) + arrowH(500,65,538) + arrowV(645,100,140)
    + box(540,140,210,80,"#E6F1FB","#185FA5",L.s4[0],L.s4[1])
    + arrowH(538,180,502)
    + box(290,140,210,80,"#F1EFE8","#5F5E5A",L.s5[0],L.s5[1])
    + arrowH(288,180,252)
    + box(40,140,210,80,"#FAEEDA","#854F0B",L.s6[0],L.s6[1])
    + arrowV(145,220,260)
    + box(40,260,210,80,"#E6F1FB","#185FA5",L.s7[0],L.s7[1])
    + arrowH(250,300,288)
    + box(290,260,210,80,"#FAEEDA","#854F0B",L.s8[0],L.s8[1])
    + arrowH(500,300,538)
    + box(540,260,210,80,"#FAEEDA","#854F0B",L.s9[0],L.s9[1])
    + arrowV(645,340,380)
    + box(540,380,210,70,"#E6F1FB","#185FA5",L.s10[0],L.s10[1])
    + arrowH(538,415,502)
    + box(290,380,210,70,"#E1F5EE","#0F6E56",L.s11[0],L.s11[1])
    + '</svg>';
}

function principlesHtml(lang) {
  var ES = lang === "es";
  var items = ES ? [
    ["Sin manejo físico por el importador","La fruta fluye productor → shipper → bodega → cliente. Ground-Fresh nunca tiene contacto físico con el producto."],
    ["Trazabilidad continua","Los números van ligados en cada etapa, desde el origen hasta la entrega."],
    ["Prevendido antes de exportar","Nada sale del origen sin estar vendido; no se mantienen inventarios en Texas."],
    ["Cadena totalmente certificada","Todos los participantes de la cadena de suministro están certificados orgánicos."],
    ["Verificación de documentos y cantidades al recibir","La bodega concilia cantidades, números de pallet y documentos contra el BOL de origen antes de ingresar el producto bajo el Lote# asignado."],
  ] : [
    ["No physical handling by importer","Fruit flows grower → shipper → warehouse → client. Ground-Fresh never has physical contact with the product."],
    ["Continuous traceability","Numbers stay linked at every stage, from origin to delivery."],
    ["Pre-sold before export","Nothing leaves origin unsold; no inventory is kept in Texas."],
    ["Fully certified chain","All supply-chain participants are certified organic."],
    ["Document & quantity verification at receipt","Warehouse reconciles quantities, pallet numbers and documents against the origin BOL before entering product under the assigned Lot#."],
  ];
  var h = '<div class="section"><div class="section-title">' + (ES?"Principios de Integridad Orgánica":"Organic Integrity Principles") + '</div><table><tbody>';
  for (var i=0;i<items.length;i++){ h += '<tr><td style="width:38%;font-weight:700;color:#1B3A4B">'+escHtml(items[i][0])+'</td><td>'+escHtml(items[i][1])+'</td></tr>'; }
  return h + '</tbody></table></div>';
}
function buildHtml(data, audit, lang) {
  var t = T[lang === "es" ? "es" : "en"];
  var co = data.company || {}; var cert = data.cert || {};
  var addr = [co.addressLine, co.city, co.state, co.zip, co.country].filter(Boolean).join(", ");
  var certNo = co.organicCert || cert.certNumber || "";
  var verStr = audit && audit.year ? ((lang === "es" ? "Versión " : "Version ") + audit.year + "-" + (Number(audit.year) + 1)) : "";
  var h = '<!DOCTYPE html><html><head><meta charset="utf-8"><style>' + reportStyles()
    + '.page{max-width:1040px}'
    + '@page{size:letter landscape;margin:0.45in 0.5in}'
    + '.gi-table td{padding:6px 8px;border-bottom:1px solid #e5e7eb;font-size:11px}'
    + '.gi-table td.k{width:30%;font-weight:700;color:#1B3A4B;text-transform:uppercase;letter-spacing:0.4px;font-size:9.5px}'
    + '.flow-wrap{margin-top:6px;border:1px solid #d1d5db;border-radius:6px;padding:6px;text-align:center}'
    + '.two-col{display:flex;gap:16px;align-items:flex-start;margin-top:10px}'
    + '.col-left{flex:1.3;min-width:0}'
    + '.col-right{flex:1;min-width:0}'
    + '.col-right table{font-size:9px}'
    + '.col-right td{padding:4px 6px}'
    + '.flow-wrap svg{max-width:680px;width:100%;height:auto}'
    + '.section{margin-top:10px}'
    + '.gi-table td{padding:4px 8px}'
    + '</style></head><body><div class="page">';
  h += policyHeader(t.title, (t.certNo + ": " + certNo), verStr);
  h += '<div class="section"><div class="section-title">' + t.secGeneral + '</div><table class="gi-table"><tbody>';
  h += '<tr><td class="k">' + t.legalName + '</td><td>' + escHtml(co.legalName || "") + '</td></tr>';
  h += '<tr><td class="k">' + t.address + '</td><td>' + escHtml(addr) + '</td></tr>';
  h += '<tr><td class="k">' + t.certNo + '</td><td>' + escHtml(certNo) + '</td></tr>';
  h += '<tr><td class="k">' + t.nopId + '</td><td>' + escHtml(cert.nopId || "") + '</td></tr>';
  h += '<tr><td class="k">' + t.certifier + '</td><td>' + escHtml(cert.certifier || co.organicCertifier || "") + '</td></tr>';
  h += '<tr><td class="k">' + t.scope + '</td><td>' + escHtml(data.scopeText || t.scopeVal) + '</td></tr>';
  h += '<tr><td class="k">' + t.opType + '</td><td>' + escHtml(t.opTypeVal) + '</td></tr>';
  h += '</tbody></table></div>';
  h += '<div class="two-col">';
  h += '<div class="col-left"><div class="section"><div class="section-title">' + t.secFlow + '</div><div class="flow-wrap">' + flowDiagramSvg(lang) + '</div></div></div>';
  h += '<div class="col-right">' + principlesHtml(lang) + '</div>';
  h += '</div>';
  h += policyFooter();
  return h + '</div></body></html>';
}

export default function GeneralInfoPanel({ lang, audit, onClose, onLogReport, initialParams }) {
  const t = T[lang === "es" ? "es" : "en"];
  const [data, setData] = useState(null);
  const [scopeText, setScopeText] = useState((initialParams && initialParams.scopeText) || t.scopeVal);
  const [busy, setBusy] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const lastBlobRef = useRef(null); const lastFilenameRef = useRef("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const profiles = await loadFromSupabase("company_profile");
        const company = (profiles || []).find(p => String(p.id) === "gfi") || (profiles || [])[0] || {};
        const certs = await loadFromSupabase("certifications");
        const cert = (certs || []).find(c => c && c.entityType === "gfi" && c.certType === "organic") || {};
        if (alive) setData({ company, cert });
      } catch (e) {
        console.error("[GeneralInfo] load failed", e);
        if (alive) setData({ company: {}, cert: {} });
      }
    })();
    return () => { alive = false; };
  }, []);

  const fullData = useMemo(() => data ? { ...data, scopeText } : null, [data, scopeText]);
  const makeHtml = useCallback(() => buildHtml(fullData || { company:{}, cert:{} }, audit, lang), [fullData, audit, lang]);
  const previewHtml = useMemo(() => makeHtml(), [makeHtml]);
  const currentParams = useMemo(() => ({ scopeText }), [scopeText]);

  const handlePreview = () => openPreviewWindow(makeHtml());

  const handlePdf = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const blob = await htmlToPdfBlob(makeHtml(), { orientation: "landscape", widthPx: 1056 });
      const fn = safeFilename("General_Info_" + (audit && audit.name || "")) + ".pdf";
      lastBlobRef.current = blob; lastFilenameRef.current = fn;
      downloadBlob(blob, fn);
      onLogReport({ type: "general_info", title: (lang==="es"?"PDF descargado":"PDF downloaded") + " · " + fn, params: currentParams, blob: blob, filename: fn, mime: "application/pdf" });
    } catch (e) {
      console.error("[GeneralInfo] pdf failed", e);
      window.alert((lang==="es"?"Error generando PDF: ":"PDF generation failed: ") + (e.message || String(e)));
    } finally { setBusy(false); }
  };

  const company = (data && data.company) || {};
  /* 2026-09-30 — excludes Sweetleaf/automated accounts, same fix as
     OrganicAuditRegistry.js's own emailAccounts. */
  const emailAccounts = ((company && company.emailAccounts) || []).filter(function (a) { var n = (a.name || "").toLowerCase(); return n.indexOf("sweetleaf") < 0 && n.indexOf("automat") < 0; });
  const fromEmail = (company && (company.outboundEmail || company.email)) || "";
  const signatures = (company && company.emailSignatures) || [];

  const inp = { padding: "8px 10px", borderRadius: 6, border: "1px solid var(--color-border)", background: "var(--color-bg-input)", color: "var(--color-text)", fontSize: 13, width: "100%" };
  const btnPrimary = { padding: "8px 16px", borderRadius: 6, border: "none", background: "#1B3A4B", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 13 };
  const btnSecondary = { padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text)", cursor: "pointer", fontSize: 13 };

  return (
    <DraggableModal title={t.title + " · " + (audit && audit.name || "")} onClose={onClose} zIndex={10003}>
      <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
        {!data ? (
          <div style={{ color: "var(--color-text-muted)", fontStyle: "italic", padding: 20 }}>{t.loading}</div>
        ) : (
          <>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{t.scope}</div>
              <input value={scopeText} onChange={e => setScopeText(e.target.value)} style={inp} />
            </div>
            <div style={{ border: "1px solid var(--color-border)", borderRadius: 6, overflow: "hidden", background: "#fff", height: 420 }}>
              <iframe title="general-info-preview" srcDoc={previewHtml} style={{ width: "100%", height: "100%", border: 0 }} />
            </div>
          </>
        )}
      </div>
      <div style={{ padding: "12px 22px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <div style={{ fontSize: 11, color: "var(--color-text-muted)" }}>{busy ? t.sending : ""}</div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onClose} style={btnSecondary}>{t.close}</button>
          <button onClick={handlePreview} style={btnSecondary}>{t.preview}</button>
          <button onClick={() => setShowEmail(true)} style={btnSecondary}>{t.email}</button>
          <button onClick={handlePdf} style={btnPrimary} disabled={busy}>{busy ? t.busy : t.pdf}</button>
        </div>
      </div>

      {showEmail && (
        <EmailDialog
          lang={lang}
          title={t.email}
          recipients={(audit && audit.auditorEmail) ? [{ name: audit.auditorName || "", email: audit.auditorEmail, checked: true }] : []}
          docOptions={[{ key: "report", label: t.title, checked: true }]}
          defaultSubject={t.emailSubject + " — " + (audit && audit.name || "")}
          defaultBody={t.emailBody + (audit && audit.name || "") + "."}
          fromEmail={fromEmail}
          emailAccounts={emailAccounts}
          signatures={signatures}
          postProcessHtmlBody={(html, resolvedFrom) => appendDisclosureByAccount(html, resolvedFrom, company, null)}
          buildAttachments={async () => {
            const blob = await htmlToPdfBlob(makeHtml(), { orientation: "landscape", widthPx: 1056 });
            const fn = safeFilename("General_Info_" + (audit && audit.name || "")) + ".pdf";
            lastBlobRef.current = blob; lastFilenameRef.current = fn;
            return [{ filename: fn, blob: blob, mimeType: "application/pdf" }];
          }}
          onSent={() => {
            const to = (audit && audit.auditorEmail) ? [audit.auditorEmail] : [];
            onLogReport({ type: "general_info", title: lang === "es" ? "Enviado por correo" : "Sent by email", recipients: to, params: currentParams, blob: lastBlobRef.current, filename: lastFilenameRef.current, mime: "application/pdf" });
            setShowEmail(false);
          }}
          onClose={() => setShowEmail(false)}
        />
      )}
    </DraggableModal>
  );
}
